import { API_ENDPOINTS } from '@/constants/api-endpoints';
import {
  getAllBotsData,
  getArchive,
  getBotBlog,
  getBotData,
  getBotWebinar,
  getDashboardScore,
  getDemoDetails,
  getEntries,
  getHelpSection,
  getInternalAgent,
  getInternalAgentSummary,
  getLinks,
  getLinksForCTA,
  getLLMSetting,
  getQA,
  getThreadDetails,
  getTrainedData,
  getVendorId,
  getVideoLinks,
  improveAnswer,
  listPersonas,
  removeTrainedData,
  uploadVendorFile,
  upsertEntry,
} from '@/services/bots-refactor';
import { updateLinkOptions } from '@/services/page-cta';
import { IBaseQueryParams } from '@/types/common';
import { authRetryConfig } from '@/utils/queryRetry';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export const useGetVendorId = (enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getVendorId],
    queryFn: getVendorId,
    enabled,
    ...authRetryConfig,
    throwOnError: false, // Don't throw error to parent components
  });
};

export const useGetDashboardScore = (
  chatToken: string,
  dateParams?: {
    startDate?: Date;
    endDate?: Date;
    preset?: string;
  },
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getDashboardScore, chatToken, dateParams],
    queryFn: () => getDashboardScore(chatToken, dateParams),
    enabled: !!chatToken,
    ...authRetryConfig,
  });
};

export const useGetHelpSection = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getHelpSection(chatToken), chatToken],
    queryFn: () => getHelpSection(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetBotBlog = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getBotBlog(chatToken), chatToken],
    queryFn: () => getBotBlog(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetBotWebinar = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getBotWebinar(chatToken), chatToken],
    queryFn: () => getBotWebinar(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetDemoDetails = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getDemoDetails(chatToken), chatToken],
    queryFn: () => getDemoDetails(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetBotData = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getBotData(chatToken), chatToken],
    queryFn: () => getBotData(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetLLMSetting = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getLLMSetting(chatToken), chatToken],
    queryFn: () => getLLMSetting(chatToken),
    enabled: !!chatToken,
  });
};

export const useListPersonas = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.listPersonas(chatToken), chatToken],
    queryFn: () => listPersonas(chatToken),
    enabled: !!chatToken,
  });
};

interface IGetLinksParams extends IBaseQueryParams {
  status?: string;
}

export const useGetLinks = (chat_token: string, queryParams: IGetLinksParams) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getLinks, chat_token, queryParams],
    queryFn: () =>
      getLinks({
        ...queryParams,
        chat_token,
      }),
    enabled: !!chat_token,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  });
};

export const useListLinks = (chat_token: string) => {
  const [queryParams, setQueryParams] = useState<IBaseQueryParams>({
    page: 1,
    limit: 10,
    searchText: '',
  });

  return {
    ...useQuery({
      queryKey: [API_ENDPOINTS.getLinks, chat_token, queryParams],
      queryFn: () =>
        getLinksForCTA({
          ...queryParams,
          chat_token,
        }),
      enabled: !!chat_token,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60,
    }),
    queryParams,
    setQueryParams,
  };
};

export const useUpdateLinkOptions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateLinkOptions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getLinks] });
    },
  });
};

export const useGetTrainedData = (chat_token: string) => {
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    searchText: '',
    chat_token,
  });

  return {
    ...useQuery({
      queryKey: [API_ENDPOINTS.getTrainedData, chat_token, queryParams],
      queryFn: () => getTrainedData(queryParams),
      enabled: !!chat_token,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60,
    }),
    queryParams,
    setQueryParams,
  };
};

export const useGetVideoLinks = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getVideoLinks(chatToken), chatToken],
    queryFn: () => getVideoLinks(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetQA = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getQA(chatToken), chatToken],
    queryFn: () => getQA(chatToken),
    enabled: !!chatToken,
  });
};

export const useGetThreadDetails = (chatToken: string, filter: any) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getThreadDetails(chatToken), chatToken, filter],
    queryFn: () => getThreadDetails(chatToken, filter),
    enabled: !!chatToken,
  });
};

export const useRemoveTrainedData = () => {
  return useMutation({
    mutationFn: ({ file_id, chatToken }: { file_id: number; chatToken: any }) =>
      removeTrainedData(file_id, chatToken),
  });
};

export const useGetArchive = () => {
  return useMutation({
    mutationFn: getArchive,
  });
};

export const useGetAllBotsData = (vendorId: number) => {
  const enabled = !!vendorId && vendorId > 0;

  return useQuery({
    queryKey: [API_ENDPOINTS.getAllBotsData(vendorId), vendorId],
    queryFn: () => getAllBotsData(vendorId),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
  });
};

export const useGetInternalAgent = () => {
  return useMutation({
    mutationFn: (fake: string) => getInternalAgent(),
  });
};

export const useGetInternalAgentSummary = (accessToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getInternalAgentSummary(accessToken), accessToken],
    queryFn: () => getInternalAgentSummary(accessToken),
    enabled: !!accessToken,
  });
};

export const useUploadVendorFile = () => {
  return useMutation({
    mutationFn: uploadVendorFile,
  });
};

export const useUpsertEntry = () => {
  return useMutation({
    mutationFn: upsertEntry,
  });
};

export const useGetEntries = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getEntries(chatToken), chatToken],
    queryFn: () => getEntries(chatToken),
    enabled: !!chatToken,
  });
};

export const useImproveAnswer = () => {
  return useMutation({
    mutationFn: improveAnswer,
  });
};
